// Schemas Zod réutilisables — validation côté formulaire et côté API.

import {
  ACCOUNTING_PAYMENT_MODES,
  ACCOUNTING_TYPES,
  BLOOD_GROUPS,
  CONTRACT_TYPES,
  PAYMENT_METHODS,
  PHARMACY_PLANS,
  PHONE_237_REGEX,
  PIN_LENGTH,
  REMINDER_FREQUENCIES,
  USER_ROLES,
  VITAL_SIGN_TYPES,
} from '@pharmaconnect/config'
import { z } from 'zod'

export const Phone237Schema = z
  .string()
  .regex(PHONE_237_REGEX, 'Numéro camerounais invalide (format +237XXXXXXXXX)')

export const UUIDSchema = z.string().uuid('UUID invalide')

export const PinSchema = z
  .string()
  .length(PIN_LENGTH, `PIN à ${PIN_LENGTH} chiffres requis`)
  .regex(/^[0-9]+$/, 'PIN numérique uniquement')

export const FCFAAmountSchema = z
  .number()
  .int('Montant en FCFA (entier)')
  .nonnegative('Montant positif requis')

export const PositiveFCFASchema = FCFAAmountSchema.positive('Montant strictement positif requis')

// --- Auth ---
export const SendOTPSchema = z.object({
  phone: Phone237Schema,
})

export const VerifyOTPSchema = z.object({
  phone: Phone237Schema,
  code: z
    .string()
    .length(6)
    .regex(/^[0-9]+$/),
})

export const CashierLoginSchema = z.object({
  email: z.string().email('Adresse email invalide').max(255),
  pin: PinSchema,
})

export const EmailPasswordLoginSchema = z.object({
  email: z.string().email('Adresse email invalide').max(255),
  password: z.string().min(8, 'Mot de passe trop court (8 caractères minimum)').max(128),
})

export type EmailPasswordLoginInput = z.infer<typeof EmailPasswordLoginSchema>

// --- Pharmacie / utilisateur ---
export const PharmacyCreateSchema = z.object({
  name: z.string().min(2).max(120),
  owner_id: UUIDSchema.optional(),
  address: z.string().max(255).optional(),
  city: z.string().min(2).max(80).default('Douala'),
  phone: Phone237Schema,
  plan: z.enum(PHARMACY_PLANS).default('free'),
  delivery_zone: z.number().int().min(1).max(50).default(5),
})

export const UserProfileCreateSchema = z.object({
  role: z.enum(USER_ROLES),
  pharmacy_id: UUIDSchema.optional(),
  full_name: z.string().min(2).max(120),
  phone: Phone237Schema,
  avatar_url: z.string().url().optional(),
  onmc_number: z.string().optional(),
  cni_number: z.string().optional(),
})

// --- Stocks ---
export const MedicationCreateSchema = z.object({
  pharmacy_id: UUIDSchema,
  name: z.string().min(1).max(160),
  dci: z.string().max(160).optional(),
  form: z.string().max(60).optional(),
  dosage: z.string().max(60).optional(),
  barcode: z.string().max(64).optional(),
  quantity: z.number().int().nonnegative(),
  alert_threshold: z.number().int().nonnegative().default(10),
  price: PositiveFCFASchema,
  expiry_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Format YYYY-MM-DD attendu')
    .optional(),
  category: z.string().max(60).optional(),
  supplier_id: UUIDSchema.optional(),
})

// --- Caisse ---
export const SaleItemSchema = z.object({
  medication_id: UUIDSchema,
  quantity: z.number().int().positive(),
  unit_price: PositiveFCFASchema,
})

export const CreateSaleSchema = z.object({
  pharmacy_id: UUIDSchema,
  cashier_id: UUIDSchema.optional(),
  patient_id: UUIDSchema.optional(),
  payment_method: z.enum(PAYMENT_METHODS),
  discount: FCFAAmountSchema.default(0),
  prescription_id: UUIDSchema.optional(),
  items: z.array(SaleItemSchema).min(1, 'Au moins un article requis'),
})

// --- Carnet patient ---
export const PatientRecordUpdateSchema = z.object({
  blood_group: z.enum(BLOOD_GROUPS).optional(),
  weight_kg: z.number().positive().optional(),
  height_cm: z.number().int().positive().optional(),
  allergies: z.array(z.string()).optional(),
  chronic_diseases: z.array(z.string()).optional(),
  emergency_contacts: z
    .array(
      z.object({
        name: z.string().min(1),
        phone: Phone237Schema,
        relation: z.string().min(1),
      }),
    )
    .max(3)
    .optional(),
})

export const VitalSignCreateSchema = z.object({
  patient_id: UUIDSchema,
  type: z.enum(VITAL_SIGN_TYPES),
  value: z.number(),
  unit: z.string().min(1).max(16),
  measured_at: z.string().datetime().optional(),
})

export const ReminderCreateSchema = z.object({
  patient_id: UUIDSchema,
  medication_name: z.string().min(1).max(120),
  dosage: z.string().max(60).optional(),
  times: z.array(z.string().regex(/^\d{2}:\d{2}$/)).min(1),
  frequency: z.enum(REMINDER_FREQUENCIES),
  start_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  end_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
})

// --- CamerPay ---
export const PaymentLinkSchema = z.object({
  amount: PositiveFCFASchema,
  payment_method: z.enum(['orange_money', 'mtn_momo', 'card', 'paypal']),
  merchant_invoice_id: z.string().min(1).max(64),
  customer_name: z.string().min(2).max(120),
  customer_phone: Phone237Schema,
  description: z.string().max(255).optional(),
  callback_url: z.string().url(),
  return_url: z.string().url(),
})

// --- Comptabilité ---
export const AccountingEntryCreateSchema = z.object({
  pharmacy_id: UUIDSchema,
  type: z.enum(ACCOUNTING_TYPES),
  category: z.string().min(1).max(80),
  label: z.string().min(1).max(255),
  amount: PositiveFCFASchema,
  payment_mode: z.enum(ACCOUNTING_PAYMENT_MODES),
  reference: z.string().max(120).optional(),
  entry_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
})

// --- RH / Paie ---
export const EmployeeCreateSchema = z.object({
  pharmacy_id: UUIDSchema,
  user_id: UUIDSchema.optional(),
  full_name: z.string().min(2).max(120),
  role: z.string().min(1).max(80),
  contract_type: z.enum(CONTRACT_TYPES),
  contract_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  contract_end: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  gross_salary: PositiveFCFASchema,
  cnps_number: z.string().max(40).optional(),
})
